import { useState } from 'react';
import { MAIL_ART_NAMEN, type MailArt, type MailDetail, type MailListenEintrag } from '@beholder/shared/module/mail';
import { Knopf, datumZeit } from '../../core/ui';

/** Kleine Bausteine für die Mail-Seiten. Links aus Mails sind hier NIE anklickbar – nur Text. */

export function ArtMarke({ art }: { art: MailArt | null }) {
  if (!art) return <span className="marke">wird ausgewertet</span>;
  return <span className={`marke ml-art ml-art-${art}`}>{MAIL_ART_NAMEN[art]}</span>;
}

/** Warnmarken: Betrugsverdacht und „aus Spam“. */
export function Verdacht({ m }: { m: Pick<MailListenEintrag, 'verdaechtig' | 'ausSpam'> }) {
  return (
    <>
      {m.ausSpam && (
        <span className="marke ml-spam" title="Lag im Spam-Ordner des Postfachs – wird nie automatisch einsortiert">
          aus Spam
        </span>
      )}
      {m.verdaechtig && <span className="marke ml-verdacht">Betrugsverdacht</span>}
    </>
  );
}

/** Plus-Adressen eines Projekts als Text („a oder b“). */
export function PlusAdressen({ adressen }: { adressen: string[] }) {
  return (
    <>
      {adressen.map((a, i) => (
        <span key={a}>
          {i > 0 && (i === adressen.length - 1 ? ' oder ' : ', ')}
          <code>{a}</code>
        </span>
      ))}
    </>
  );
}

export function Absender({ m }: { m: Pick<MailListenEintrag, 'vonName' | 'vonAdresse'> }) {
  return (
    <span className="ml-absender">
      {m.vonName ? (
        <>
          {m.vonName} <span className="leise">‹{m.vonAdresse}›</span>
        </>
      ) : (
        m.vonAdresse || 'unbekannt'
      )}
    </span>
  );
}

export function wann(m: Pick<MailListenEintrag, 'datum' | 'eingegangenAm'>) {
  return datumZeit(m.datum ?? m.eingegangenAm);
}

/** Text in die Zwischenablage – für Link-Adressen, die man bewusst prüfen will. */
export function KopierKnopf({ text, titel = 'Kopieren' }: { text: string; titel?: string }) {
  const [ok, setOk] = useState(false);
  return (
    <Knopf
      klein
      art="leise"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          // Ohne Zwischenablage (z. B. ohne HTTPS): Text zum Markieren anbieten
          window.prompt('Zum Kopieren markieren:', text);
        }
        setOk(true);
        setTimeout(() => setOk(false), 1500);
      }}
    >
      {ok ? 'Kopiert' : titel}
    </Knopf>
  );
}

/** Links als reiner Text: Linktext, Ziel, Domain, Auffälligkeiten. Bewusst kein <a>. */
export function LinkTabelle({ m }: { m: Pick<MailDetail, 'linkListe' | 'externeBilder'> }) {
  if (!m.linkListe.length) return <p className="leise">Die Mail enthält keine Links.</p>;
  return (
    <div className="stapel ml-links">
      <p className="leise klein">
        Links wurden nur als Text herausgezogen und nie geöffnet. Bitte keinen davon ungeprüft aufrufen.
        {m.externeBilder > 0 && ` ${m.externeBilder} Bild${m.externeBilder === 1 ? '' : 'er'} von fremden Servern wurde${m.externeBilder === 1 ? '' : 'n'} nicht geladen.`}
      </p>
      <ol className="ml-linkliste">
        {m.linkListe.map((l) => (
          <li key={l.nr} className={l.auffaellig.length ? 'ml-link-auffaellig' : ''}>
            <div className="ml-link-kopf">
              <strong>Link {l.nr}</strong>
              <span className="ml-link-text">„{l.text}“</span>
            </div>
            <div className="ml-link-ziel">
              <code>{l.ziel}</code>
              <KopierKnopf text={l.ziel} />
            </div>
            <div className="klein">
              Domain: <code>{l.domain || 'unbekannt'}</code>
            </div>
            {l.auffaellig.length > 0 && (
              <ul className="ml-hinweise">
                {l.auffaellig.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
